import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCards } from '../../hooks/useCards';
import { supabase } from '../../lib/supabase';
import { Breadcrumbs } from '../../components/navigation/Breadcrumbs';
import { Loader2 } from 'lucide-react';
import { useQuery } from '../../hooks/useQuery';
import { useDecks } from '../../hooks/useDecks';
import { Deck, PokemonCard, FilterState } from '../../types/pokemon';
import { DeckFormHeader } from '../../components/decks/DeckForm';
import { DeckCardSelector } from '../../components/decks/DeckCardSelector';
import { DeckPreviewPanel } from '../../components/decks/DeckPreviewPanel';

export function EditDeck() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { updateDeck } = useDecks();
  const [selectedCards, setSelectedCards] = useState<Map<number, number>>(new Map());
  const [filters, setFilters] = useState<FilterState>({
    pack: null,
    rarity: null,
    set: null,
    search: '',
    type: null,
    energy: null,
    showMissing: false
  });
  const [selectedCardsData, setSelectedCardsData] = useState<PokemonCard[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');

  const { data: deck, loading: deckLoading } = useQuery<Deck>(
    'decks',
    { id },
    { 
      single: true,
      withRelations: `*, cards:deck_cards(quantity, card:cards(*))`
    }
  );

  const {
    cards,
    loading: cardsLoading,
    filterOptions,
  } = useCards(filters);

  const totalCards = Array.from(selectedCards.values()).reduce((sum, quantity) => sum + quantity, 0);

  useEffect(() => {
    if (deck) {
      const initialSelectedCards = new Map();
      deck.cards?.forEach(card => {
        if (card.card) {
          initialSelectedCards.set(card.card.id, card.quantity);
        }
      });
      setSelectedCards(initialSelectedCards);
      setIsPublic(deck.is_public || false);
      setDescription(deck.description || '');
      setName(deck.name);
      setHasChanges(false);
    }
  }, [deck]);

  useEffect(() => {
    const fetchSelectedCards = async () => {
      const cardIds = Array.from(selectedCards.keys());
      if (cardIds.length === 0) {
        setSelectedCardsData([]);
        return;
      }

      const { data } = await supabase
        .from('cards')
        .select('*')
        .in('id', cardIds);

      setSelectedCardsData(data || []);
    };

    fetchSelectedCards();
  }, [selectedCards]);

  useEffect(() => {
    if (deck) {
      setHasChanges(
        description !== (deck.description || '') ||
        name !== deck.name ||
        isPublic !== (deck.is_public || false)
      );
    }
  }, [description, name, isPublic, deck]);

  const handleCardSelect = (cardId: number, quantity: number) => {
    const newSelectedCards = new Map(selectedCards);
    if (quantity === 0) {
      newSelectedCards.delete(cardId);
    } else {
      const currentTotal = Array.from(newSelectedCards.entries()).reduce((sum, [id, qty]) => {
        return sum + (id === cardId ? 0 : qty);
      }, 0);
      
      if (currentTotal + quantity <= 20) {
        newSelectedCards.set(cardId, quantity);
      }
    }
    setSelectedCards(newSelectedCards);
    setHasChanges(true);
  };

  const handleRemoveCard = (cardId: number) => {
    const newSelectedCards = new Map(selectedCards);
    newSelectedCards.delete(cardId);
    setSelectedCards(newSelectedCards);
    setHasChanges(true);
  };

  const handleSubmit = async () => {
    if (!deck || isSubmitting) return;
    
    if (totalCards !== 20) {
      alert('Le deck doit contenir exactement 20 cartes');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Update deck details
      await updateDeck(deck.id, {
        name,
        description,
        is_public: isPublic
      });

      // Delete all existing cards
      const { error: deleteError } = await supabase
        .from('deck_cards')
        .delete()
        .eq('deck_id', deck.id);

      if (deleteError) throw deleteError;

      // Add new cards
      const cardsToAdd = Array.from(selectedCards.entries()).map(([cardId, quantity]) => ({
        deck_id: deck.id,
        card_id: cardId,
        quantity
      }));

      if (cardsToAdd.length > 0) {
        const { error: insertError } = await supabase
          .from('deck_cards')
          .insert(cardsToAdd);

        if (insertError) throw insertError;
      }

      setHasChanges(false);
      navigate(`/decks/${deck.id}`);
    } catch (error) {
      console.error('Error updating deck:', error);
      alert("Erreur lors de la mise à jour du deck");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (deckLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!deck) {
    return (
      <div className="text-center py-12">
        <p className="text-danger-500">Impossible de charger le deck</p>
      </div>
    );
  }

  const deckCards = selectedCardsData.map(card => ({
    card_id: card.id,
    quantity: selectedCards.get(card.id) || 0,
    card
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumbs
        items={[
          { label: 'Decks', path: '/decks' },
          { label: deck.name, path: `/decks/${deck.id}` },
          { label: 'Modifier', path: `/decks/${deck.id}/edit` }
        ]}
      />

      <div className="mt-8">
        <DeckFormHeader
          name={name}
          onNameChange={setName}
          description={description}
          onDescriptionChange={setDescription}
          isPublic={isPublic}
          onVisibilityChange={setIsPublic}
          disabled={isSubmitting}
          title="Modifier le deck"
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <DeckCardSelector
              filters={filters}
              onFilterChange={setFilters}
              cards={cards}
              loading={cardsLoading}
              selectedCards={selectedCards}
              onUpdateQuantity={handleCardSelect}
              totalCards={totalCards}
              filterOptions={filterOptions}
            />
          </div>

          <div className="lg:col-span-1">
            <DeckPreviewPanel
              cards={deckCards}
              totalCards={totalCards}
              onSubmit={handleSubmit}
              onCancel={() => navigate(`/decks/${deck.id}`)}
              submitLabel="Mettre à jour"
              isSubmitting={isSubmitting}
              showCancelButton={true}
              hasChanges={hasChanges}
              onRemoveCard={handleRemoveCard}
            />
          </div>
        </div>
      </div>
    </div>
  );
}